import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { hrmApi, branchesApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency } from '../../utils/currency';

import { Modal } from '../../components/Modal';

type Tab = 'today' | 'my_records' | 'timesheet' | 'devices';

/**
 * Attendance — FR-HR-02
 *
 * Four tabs:
 * - Today: hero clock-in/out card for self
 * - My Records: personal history with date range
 * - Team Timesheet: manager roll-up across team
 * - Devices: biometric / hardware terminal management
 */
export function AttendancePage() {
  const [tab, setTab] = useState<Tab>('today');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'today',      label: 'Today' },
    { key: 'my_records', label: 'My records' },
    { key: 'timesheet',  label: 'Team timesheet' },
    { key: 'devices',    label: '📡 Devices' },
  ];

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="People"
        title="Attendance"
        subtitle="Clock in / out, review records, and roll up team hours."
      />

      {/* Tab strip */}
      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 text-sm font-semibold rounded-pill transition-all',
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'today'      && <TodayTab />}
      {tab === 'my_records' && <MyRecordsTab />}
      {tab === 'timesheet'  && <TimesheetTab />}
      {tab === 'devices'    && <DevicesTab />}
    </div>
  );
}

// ============================================================
// TodayTab — clock in/out hero
// ============================================================
function TodayTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['attendance-today'],
    queryFn: () => hrmApi.getTodayAttendance(),
  });

  const clockInMut = useMutation({
    mutationFn: () => hrmApi.clockIn({ method: 'web' }),
    onSuccess: (d) => {
      toast.success(d.isLate ? 'Clocked in (late)' : 'Clocked in');
      qc.invalidateQueries({ queryKey: ['attendance-today'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const clockOutMut = useMutation({
    mutationFn: () => hrmApi.clockOut({}),
    onSuccess: (d) => {
      toast.success(`Clocked out · ${d.hoursWorked}h worked${d.overtimeHours > 0 ? ` (${d.overtimeHours}h OT)` : ''}`, { duration: 5000 });
      qc.invalidateQueries({ queryKey: ['attendance-today'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  // User has no employee record (e.g. system admin) — show helpful message
  if (d?.noEmployeeRecord) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="card p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-canvas-subtle border-2 border-dashed border-ink-200 flex items-center justify-center mx-auto mb-4 text-2xl">👤</div>
          <h3 className="font-bold text-ink-900 mb-2">No employee record linked</h3>
          <p className="text-sm text-ink-500 leading-relaxed">
            Your user account isn't linked to an employee record. Clock-in is only available for staff employees.
            To enable this, go to <strong>HRM → Employees</strong> and link your user account to an employee profile.
          </p>
        </div>
      </div>
    );
  }

  const rec = d.record;
  const today = new Date();

  return (
    <div className="max-w-lg mx-auto">
      <div className="text-center mb-6">
        <div className="eyebrow text-ink-500 mb-2">
          {today.toLocaleDateString(undefined, { weekday: 'long' })}
        </div>
        <h2 className="font-display font-bold text-3xl text-ink-900 tracking-tight">
          {today.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
        </h2>
      </div>

      {!rec ? (
        <div className="card p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-canvas-subtle text-ink-500 flex items-center justify-center mx-auto mb-4">
            <ClockIcon />
          </div>
          <div className="font-semibold text-ink-900 mb-1">Ready to start</div>
          <div className="text-sm text-ink-500 mb-6">
            You haven't clocked in yet today. Clock in to begin tracking hours.
          </div>
          <button className="btn-primary !py-3 !px-8 !text-base"
            disabled={clockInMut.isPending} onClick={() => clockInMut.mutate()}>
            {clockInMut.isPending ? 'Clocking in…' : 'Clock in'}
          </button>
        </div>
      ) : !rec.clock_out ? (
        <div className="card overflow-hidden">
          <div className="p-8 text-center bg-gradient-to-b from-sage-50 to-canvas-elevated">
            <div className="w-14 h-14 rounded-full bg-sage-500 text-white flex items-center justify-center mx-auto mb-4 shadow-sm">
              <CheckIcon />
            </div>
            <div className="eyebrow text-sage-700 mb-1">Clocked in</div>
            <div className="font-display font-bold text-3xl text-ink-900 tabular-nums tracking-tight">
              {new Date(rec.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            {rec.status === 'late' && (
              <div className="mt-3">
                <StatusBadge status="Late" tone="warning" />
              </div>
            )}
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex justify-center">
            <button className="btn-primary !py-3 !px-8 !text-base"
              disabled={clockOutMut.isPending} onClick={() => clockOutMut.mutate()}>
              {clockOutMut.isPending ? 'Clocking out…' : 'Clock out'}
            </button>
          </div>
        </div>
      ) : (
        <div className="card p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-sage-500 text-white flex items-center justify-center mx-auto mb-4 shadow-sm">
            <CheckIcon />
          </div>
          <div className="eyebrow text-sage-700 mb-2">Day complete</div>
          <div className="font-display font-bold text-4xl text-ink-900 tabular-nums tracking-tight">
            {parseFloat(rec.hours_worked).toFixed(2)}<span className="text-2xl text-ink-500 ml-1">h</span>
          </div>
          <div className="text-sm text-ink-500 mt-3 font-mono tabular-nums">
            In {new Date(rec.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {' · '}
            Out {new Date(rec.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          {parseFloat(rec.overtime_hours || 0) > 0 && (
            <div className="mt-3">
              <span className="badge bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200">
                {parseFloat(rec.overtime_hours).toFixed(2)}h overtime
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MyRecordsTab — personal history
// ============================================================
function MyRecordsTab() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); })();
  const [fromDate, setFromDate] = useState(monthAgo);
  const [toDate, setToDate] = useState(today);

  const selfQ = useQuery({
    queryKey: ['attendance-today'],
    queryFn: () => hrmApi.getTodayAttendance(),
  });
  const employeeId = selfQ.data?.employeeId;

  const recordsQ = useQuery({
    queryKey: ['attendance-records', employeeId, fromDate, toDate],
    queryFn: () => hrmApi.listAttendance(employeeId!, { fromDate, toDate, limit: 200 }),
    enabled: !!employeeId,
  });

  const kpis = useMemo(() => {
    const rows = recordsQ.data ?? [];
    let present = 0, late = 0, absent = 0, totalHours = 0, totalOT = 0;
    for (const r of rows) {
      if (r.status === 'present' || r.status === 'half_day') present++;
      if (r.status === 'late') late++;
      if (r.status === 'absent') absent++;
      totalHours += parseFloat(r.hours_worked) || 0;
      totalOT += parseFloat(r.overtime_hours) || 0;
    }
    return { present, late, absent, totalHours, totalOT };
  }, [recordsQ.data]);

  return (
    <div>
      <div className="card p-4 mb-6 flex gap-3 items-end flex-wrap">
        <div>
          <label className="label">From</label>
          <input type="date" className="input w-44" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-44" value={toDate}
            onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {/* KPI strip */}
      {recordsQ.data?.length ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiTile label="Days present" value={kpis.present} accent="sage" />
          <KpiTile label="Days late" value={kpis.late}
            accent={kpis.late > 0 ? 'amber' : 'default'} />
          <KpiTile label="Total hours" value={kpis.totalHours.toFixed(1)} />
          <KpiTile label="Overtime hours" value={kpis.totalOT.toFixed(1)}
            accent={kpis.totalOT > 0 ? 'amber' : 'default'} />
        </div>
      ) : null}

      {!employeeId ? (
        <div className="card">
          <EmptyState
            message="No employee record linked"
            hint="Your user account needs an employee profile to view attendance."
          />
        </div>
      ) : recordsQ.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : recordsQ.error ? (
        <ErrorState error={recordsQ.error} />
      ) : !recordsQ.data?.length ? (
        <div className="card">
          <EmptyState
            message="No attendance records in this range"
            hint="Try a wider date range."
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>In</th>
                  <th>Out</th>
                  <th className="text-right">Hours</th>
                  <th className="text-right">OT</th>
                  <th>Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(recordsQ.data ?? []).map((r: any) => (
                  <tr key={r.id}>
                    <td className="text-sm text-ink-700">
                      {new Date(r.work_date).toLocaleDateString(undefined, {
                        weekday: 'short', month: 'short', day: 'numeric',
                      })}
                    </td>
                    <td className="font-mono text-sm text-ink-600 tabular-nums">
                      {r.clock_in ? new Date(r.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="font-mono text-sm text-ink-600 tabular-nums">
                      {r.clock_out ? new Date(r.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="text-right font-mono font-semibold tabular-nums">
                      {r.hours_worked ? parseFloat(r.hours_worked).toFixed(2) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-right font-mono text-warning tabular-nums text-sm">
                      {r.overtime_hours && parseFloat(r.overtime_hours) > 0 ? parseFloat(r.overtime_hours).toFixed(2) : ''}
                    </td>
                    <td><AttendanceStatusBadge status={r.status} /></td>
                    <td className="text-xs text-ink-600 max-w-xs truncate">
                      {r.regularized_at && (
                        <span className="text-warning text-2xs block font-medium">
                          Regularized: {r.regularized_reason}
                        </span>
                      )}
                      {r.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TimesheetTab — team roll-up
// ============================================================
function TimesheetTab() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = (() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); })();
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);

  const q = useQuery({
    queryKey: ['timesheet', fromDate, toDate],
    queryFn: () => hrmApi.getTimesheet({ fromDate, toDate }),
  });

  const kpis = useMemo(() => {
    const rows = q.data?.rows ?? [];
    let totalHours = 0, totalOT = 0, totalLate = 0, employees = rows.length;
    for (const r of rows) {
      totalHours += r.totalHours || 0;
      totalOT += r.totalOvertime || 0;
      totalLate += r.daysLate || 0;
    }
    return { employees, totalHours, totalOT, totalLate };
  }, [q.data]);

  return (
    <div>
      <div className="card p-4 mb-6 flex gap-3 items-end flex-wrap">
        <div>
          <label className="label">From</label>
          <input type="date" className="input w-44" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-44" value={toDate}
            onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {q.data?.rows?.length ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiTile label="Employees" value={kpis.employees} />
          <KpiTile label="Total hours" value={kpis.totalHours.toFixed(1)} accent="sage" />
          <KpiTile label="Overtime hours" value={kpis.totalOT.toFixed(1)}
            accent={kpis.totalOT > 0 ? 'amber' : 'default'} />
          <KpiTile label="Late days" value={kpis.totalLate}
            accent={kpis.totalLate > 0 ? 'amber' : 'default'} />
        </div>
      ) : null}

      {q.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : q.error ? (
        <ErrorState error={q.error} />
      ) : !q.data?.rows?.length ? (
        <div className="card"><EmptyState message="No employees in this period" /></div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th className="text-right">Present</th>
                  <th className="text-right">Half</th>
                  <th className="text-right">Late</th>
                  <th className="text-right">Leave</th>
                  <th className="text-right">Hours</th>
                  <th className="text-right">OT</th>
                </tr>
              </thead>
              <tbody>
                {(q.data?.rows ?? []).map((r: any) => (
                  <tr key={r.employeeId}>
                    <td>
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar name={r.fullName} />
                        <div className="min-w-0">
                          <div className="font-semibold text-ink-900 truncate">{r.fullName}</div>
                          <div className="font-mono text-2xs text-ink-500">
                            {r.employeeCode}{r.designation && ` · ${r.designation}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-ink-700">
                      {r.departmentName ?? <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-right font-mono tabular-nums">{r.daysPresent}</td>
                    <td className="text-right font-mono text-sm text-ink-500 tabular-nums">
                      {r.daysHalf || ''}
                    </td>
                    <td className="text-right font-mono text-sm text-warning tabular-nums">
                      {r.daysLate || ''}
                    </td>
                    <td className="text-right font-mono text-sm text-ink-500 tabular-nums">
                      {r.daysLeave || ''}
                    </td>
                    <td className="text-right font-mono font-semibold tabular-nums">
                      {r.totalHours.toFixed(1)}
                    </td>
                    <td className="text-right font-mono text-sm text-warning tabular-nums">
                      {r.totalOvertime > 0 ? r.totalOvertime.toFixed(1) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// AttendanceStatusBadge — tone mapping
// ============================================================
function AttendanceStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'present'  ? 'sage' :
    status === 'late'     ? 'warning' :
    status === 'half_day' ? 'warning' :
    status === 'absent'   ? 'danger' :
    status === 'on_leave' ? 'info' :
                            'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// EmployeeAvatar (shared inline — same as EmployeesPage)
// ============================================================
function EmployeeAvatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = [
    'bg-sage-100 text-sage-700',
    'bg-amber-100 text-amber-800',
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-rose-100 text-rose-700',
    'bg-teal-100 text-teal-700',
  ];
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-2xs font-semibold flex-shrink-0 ${tones[hash % tones.length]}`}>
      {initials}
    </div>
  );
}

// ============================================================
// Shared
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

const iconProps = {
  width: 24, height: 24, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function ClockIcon() {
  return <svg {...iconProps}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}
function CheckIcon() {
  return <svg {...iconProps} strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
}

// ============================================================
// DevicesTab — biometric / hardware terminal management
// ============================================================
const PROTOCOLS = [
  { value: 'iclock', label: 'iClock (ZKTeco HTTP push)', desc: 'ZKTeco K40, F22, K14, MB360 and most ZKTeco series' },
  { value: 'webhook', label: 'Generic webhook', desc: 'Any machine that supports HTTP POST with JSON' },
  { value: 'sdk', label: 'SDK relay', desc: 'Anviz, eSSL and vendor SDK-based integrations' },
];

function DevicesTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newApiKey, setNewApiKey] = useState<{ id: string; code: string; key: string } | null>(null);
  const [deleting, setDeleting] = useState<any | null>(null);

  const devicesQ = useQuery({ queryKey: ['attendance-devices'], queryFn: hrmApi.listAttendanceDevices });

  const createMut = useMutation({
    mutationFn: (p: any) => hrmApi.createAttendanceDevice(p),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['attendance-devices'] });
      setShowCreate(false);
      setNewApiKey({ id: d.id, code: d.device_code, key: d.apiKey });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const regenMut = useMutation({
    mutationFn: (id: string) => hrmApi.regenerateDeviceKey(id),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['attendance-devices'] }); setNewApiKey({ id: d.id, code: d.deviceCode, key: d.apiKey }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => hrmApi.updateAttendanceDevice(id, { status }),
    onSuccess: () => { toast.success('Device updated'); qc.invalidateQueries({ queryKey: ['attendance-devices'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => hrmApi.deleteAttendanceDevice(id),
    onSuccess: () => { toast.success('Device removed'); qc.invalidateQueries({ queryKey: ['attendance-devices'] }); setDeleting(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const apiBaseUrl = window.location.protocol + '//' + window.location.hostname + ':5002/api';

  return (
    <div className="space-y-6">
      {/* Header + create button */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold font-display text-ink-900">Attendance machines</h2>
          <p className="text-sm text-ink-500 mt-1">
            Connect biometric terminals (ZKTeco, Anviz, eSSL) to automatically push punch records.
          </p>
        </div>
        <button className="btn-primary flex-shrink-0" onClick={() => setShowCreate(true)}>+ Add device</button>
      </div>

      {/* Integration instructions */}
      <div className="card p-5 border-sage-200 bg-sage-50/30">
        <div className="eyebrow text-sage-600 mb-3">How machine integration works</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-ink-700">
          <div className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-sage-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
            <div>
              <div className="font-semibold mb-0.5">Register the device</div>
              <div className="text-ink-500 text-xs">Add it here and copy the generated API key. It's shown only once.</div>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-sage-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
            <div>
              <div className="font-semibold mb-0.5">Configure the machine</div>
              <div className="text-ink-500 text-xs">
                Set the push URL to <code className="bg-white px-1 rounded text-sage-700 font-mono">{apiBaseUrl}/hrm/attendance/push</code> and the API key as the <code className="bg-white px-1 rounded font-mono">X-Device-Key</code> header.
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-sage-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
            <div>
              <div className="font-semibold mb-0.5">Punches sync automatically</div>
              <div className="text-ink-500 text-xs">Employee clock-in/out records appear in attendance immediately. The employee code on the machine must match their code in HRM.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Push endpoint reference card */}
      <div className="card p-5">
        <div className="eyebrow text-ink-500 mb-3">Push endpoint reference</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="label">Endpoint URL</div>
            <code className="block bg-canvas-subtle rounded-lg px-3 py-2 text-xs font-mono text-sage-700 break-all">
              POST {apiBaseUrl}/hrm/attendance/push
            </code>
          </div>
          <div>
            <div className="label">Required header</div>
            <code className="block bg-canvas-subtle rounded-lg px-3 py-2 text-xs font-mono text-sage-700">
              X-Device-Key: atd_your_api_key_here
            </code>
          </div>
          <div className="col-span-full">
            <div className="label">Request body (JSON)</div>
            <pre className="bg-canvas-subtle rounded-lg px-3 py-2 text-xs font-mono text-ink-800 overflow-x-auto">{`{
  "employeeCode": "EMP-001",    // must match HRM employee code
  "punchTime": "2026-04-23T09:05:00+05:00",  // ISO 8601 with timezone
  "punchType": 0                // 0 = clock-in, 1 = clock-out
}`}</pre>
          </div>
        </div>
      </div>

      {/* Device list */}
      {devicesQ.isLoading ? <div className="card"><LoadingState rows={3} /></div>
        : devicesQ.error ? <ErrorState error={devicesQ.error} />
        : !devicesQ.data?.length ? (
          <div className="card">
            <EmptyState
              message="No devices registered"
              hint="Add your first biometric terminal to start syncing attendance automatically."
              action={<button className="btn-primary" onClick={() => setShowCreate(true)}>+ Add device</button>}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="table">
              <thead>
                <tr>
                  <th>Device</th><th>Location</th><th>Protocol</th>
                  <th>Last punch</th><th>Total punches</th><th>Status</th>
                  <th className="text-right w-52">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(devicesQ.data ?? []).map((d: any) => (
                  <tr key={d.id}>
                    <td>
                      <div className="font-semibold text-ink-900">{d.name}</div>
                      <div className="text-2xs text-ink-500 font-mono">{d.device_code}</div>
                      {d.model && <div className="text-2xs text-ink-400">{d.model}</div>}
                    </td>
                    <td className="text-sm text-ink-600">{d.location || <span className="text-ink-400">—</span>}</td>
                    <td>
                      <span className="badge bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 font-mono text-2xs">
                        {d.push_protocol}
                      </span>
                    </td>
                    <td className="text-xs text-ink-600 tabular-nums">
                      {d.last_punch_at
                        ? new Date(d.last_punch_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                        : <span className="text-ink-400 italic">Never</span>}
                    </td>
                    <td className="text-sm font-mono tabular-nums text-ink-700">
                      {(d.total_punches ?? 0).toLocaleString()}
                    </td>
                    <td><StatusBadge status={d.status} /></td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                          onClick={() => regenMut.mutate(d.id)}>
                          New key
                        </button>
                        <button
                          className={`text-xs font-medium px-2 py-1 rounded transition-colors ${d.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                          onClick={() => toggleMut.mutate({ id: d.id, status: d.status === 'active' ? 'inactive' : 'active' })}>
                          {d.status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                        <button className="text-xs font-medium px-2 py-1 rounded text-danger hover:bg-red-50 transition-colors"
                          onClick={() => setDeleting(d)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Create modal */}
      {showCreate && (
        <DeviceCreateModal
          onClose={() => setShowCreate(false)}
          onSubmit={(p) => createMut.mutate(p)}
          loading={createMut.isPending}
        />
      )}

      {/* New API key reveal modal */}
      {newApiKey && (
        <Modal
          onClose={() => setNewApiKey(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-sage-600 mb-1">Device API key — copy now</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Save your API key</h2>
            <p className="text-sm text-danger mt-1 font-semibold">⚠ This key will not be shown again.</p>
          </div>
          <div className="px-6 py-5 space-y-3">
            <div>
              <label className="label">Device code</label>
              <div className="input bg-canvas-subtle font-mono text-xs text-ink-700 select-all">{newApiKey.code}</div>
            </div>
            <div>
              <label className="label">API key (copy and configure in your machine)</label>
              <div className="flex gap-2">
                <div className="input flex-1 bg-canvas-subtle font-mono text-xs text-sage-700 select-all break-all">{newApiKey.key}</div>
                <button className="btn-secondary text-xs !px-3"
                  onClick={() => { navigator.clipboard.writeText(newApiKey.key); toast.success('Copied!'); }}>
                  Copy
                </button>
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
              Configure your machine to POST to <code className="font-mono">{apiBaseUrl}/hrm/attendance/push</code> with header <code className="font-mono">X-Device-Key: {newApiKey.key.slice(0, 12)}…</code>
            </div>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex justify-end">
            <button className="btn-primary" onClick={() => setNewApiKey(null)}>I've saved the key</button>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleting && (
        <Modal
          onClose={() => setDeleting(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-sm w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Attendance Devices</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Remove {deleting.name}?</h2>
            <p className="text-sm text-ink-500 mt-2">The device will stop receiving punches. Existing attendance records are not affected.</p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
            <button className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate(deleting.id)}>
              {deleteMut.isPending ? 'Removing…' : 'Remove device'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DeviceCreateModal({ onClose, onSubmit, loading }: {
  onClose: () => void; onSubmit: (p: any) => void; loading: boolean;
}) {
  const [form, setForm] = useState({
    deviceCode: '', name: '', location: '', model: '',
    pushProtocol: 'iclock', notes: '',
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">HRM · Attendance</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Register attendance device</h2>
        <p className="text-sm text-ink-500 mt-1">An API key will be generated and shown once after creation.</p>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-4">
        <div>
          <label className="label">Device code <span className="text-danger">*</span></label>
          <input className="input font-mono" value={form.deviceCode}
            onChange={(e) => setForm({ ...form, deviceCode: e.target.value.toUpperCase() })}
            placeholder="ZKDEV-01" />
        </div>
        <div>
          <label className="label">Display name <span className="text-danger">*</span></label>
          <input className="input" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Main entrance" />
        </div>
        <div>
          <label className="label">Location</label>
          <input className="input" value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            placeholder="Ground floor lobby" />
        </div>
        <div>
          <label className="label">Model</label>
          <input className="input" value={form.model}
            onChange={(e) => setForm({ ...form, model: e.target.value })}
            placeholder="ZKTeco K40, Anviz W2…" />
        </div>
        <div className="col-span-2">
          <label className="label">Push protocol</label>
          <div className="space-y-2">
            {PROTOCOLS.map((p) => (
              <label key={p.value}
                className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${form.pushProtocol === p.value ? 'bg-sage-50 border-sage-300' : 'border-ink-200 hover:bg-canvas-subtle'}`}>
                <input type="radio" name="protocol" value={p.value} checked={form.pushProtocol === p.value}
                  onChange={() => setForm({ ...form, pushProtocol: p.value })} className="mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-ink-900">{p.label}</div>
                  <div className="text-xs text-ink-500">{p.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <label className="label">Notes</label>
          <input className="input" value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional notes about this device" />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary"
          disabled={loading || !form.deviceCode || !form.name}
          onClick={() => onSubmit(form)}>
          {loading ? 'Registering…' : 'Register & generate key'}
        </button>
      </div>
    </Modal>
  );
}
